import type { CampaignAnalytics, CampaignAnalyticsBreakdown, CampaignAnalyticsGranularity, CampaignCounts, CampaignKpi } from '../../../api/client.ts';
import { periodDays, periodSearch, type Period } from '../../analytics/period.ts';
import { PLATFORM_LABELS } from '../../analytics/push/PushPerformanceTable.tsx';
import { CHANNEL_LABELS } from '../labels.ts';

export const ANALYTICS_TABS = ['overview', 'variants', 'funnel', 'retention', 'activity'] as const;
export type AnalyticsTab = (typeof ANALYTICS_TABS)[number];
export const TAB_LABELS: Record<AnalyticsTab, string> = { overview: 'Overview', variants: 'Variants and control', funnel: 'Funnel', retention: 'Retention', activity: 'Message activity' };

export const BREAKDOWN_LABELS: Record<CampaignAnalyticsBreakdown, string> = { channel: 'Channel', variant: 'Variant', platform: 'Platform', locale: 'Locale' };
/** Hourly series cover less than this many days (`CAMPAIGN_ANALYTICS_HOURLY_MAX_DAYS`); the API refuses longer ones. */
export const HOURLY_MAX_DAYS = 31;

export interface CampaignAnalyticsSearch {
  from?: string;
  to?: string;
  granularity?: CampaignAnalyticsGranularity;
  breakdown?: CampaignAnalyticsBreakdown;
  tab?: AnalyticsTab;
}

/** `?from&to&granularity&breakdown&tab` of the campaign analytics page; defaults (day, channel, overview) stay out of the URL. */
export const campaignAnalyticsSearch = (search: Record<string, unknown>): CampaignAnalyticsSearch => ({
  ...periodSearch(search),
  ...(search.granularity === 'hour' ? { granularity: 'hour' as const } : {}),
  ...(search.breakdown === 'variant' || search.breakdown === 'platform' || search.breakdown === 'locale' ? { breakdown: search.breakdown } : {}),
  ...(typeof search.tab === 'string' && search.tab !== 'overview' && (ANALYTICS_TABS as readonly string[]).includes(search.tab) ? { tab: search.tab as AnalyticsTab } : {}),
});

/** Days in the period are fewer than the hourly limit: «Hour» can be asked for. */
export const hourlyAllowed = (period: Period) => {
  const days = periodDays(period);
  return days !== null && days - 1 < HOURLY_MAX_DAYS;
};

/** `1,220`, `10.0 %`; null is a dash. */
export function formatMetric(format: 'count' | 'percent', value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return format === 'percent' ? `${(value * 100).toFixed(1)} %` : value.toLocaleString('en-US');
}

/** A breakdown row's key as people say it. */
export function sliceLabel(breakdown: CampaignAnalyticsBreakdown, key: string | null, data: Pick<CampaignAnalytics, 'apiIds'>): string {
  if (key === null) return breakdown === 'locale' ? 'No locale' : breakdown === 'platform' ? 'No platform' : breakdown === 'variant' ? 'No variant' : 'No channel';
  if (breakdown === 'channel') return CHANNEL_LABELS[key as keyof typeof CHANNEL_LABELS] ?? key;
  if (breakdown === 'platform') return PLATFORM_LABELS[key] ?? key;
  if (breakdown === 'variant') return variantName(key, data);
  return key;
}

/** A variant's name from the API ids (published revision); control and unknown ids as they are. */
export function variantName(apiId: string, data: Pick<CampaignAnalytics, 'apiIds'>): string {
  if (apiId === data.apiIds.control) return 'Control';
  return data.apiIds.variants.find((v) => v.apiId === apiId)?.name ?? apiId;
}

/** Counts the performance chart can draw, with the channel they belong to. */
export const SERIES_METRICS: ReadonlyArray<{ key: keyof CampaignCounts; label: string; channel?: 'push' | 'content_card' | 'in_app' }> = [
  { key: 'messagesSent', label: 'Messages sent' },
  { key: 'uniqueRecipients', label: 'Unique recipients' },
  { key: 'pushAccepted', label: 'Push accepted by provider', channel: 'push' },
  { key: 'pushDirectOpens', label: 'Push direct opens', channel: 'push' },
  { key: 'pushInfluencedOpens', label: 'Push influenced opens', channel: 'push' },
  { key: 'cardsCreated', label: 'Cards created', channel: 'content_card' },
  { key: 'cardImpressions', label: 'Card impressions', channel: 'content_card' },
  { key: 'iamImpressions', label: 'In-app impressions', channel: 'in_app' },
  { key: 'clicks', label: 'Clicks (cards and in-app)' },
  { key: 'conversions', label: 'Conversions (primary)' },
];

/** Channel-specific KPIs and series only for channels the campaign uses or that have numbers. */
export const usesChannel = (data: Pick<CampaignAnalytics, 'campaign' | 'kpis'>, channel: CampaignKpi['channel']) => !channel || data.campaign.channels.includes(channel) || data.kpis.some((k) => k.channel === channel);

/** `3 days`, `1 hour`, `90 min` for a conversion window. */
export function formatWindow(seconds: number): string {
  const unit = (n: number, word: string) => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;
  if (seconds % 86_400 === 0) return unit(seconds / 86_400, 'day');
  if (seconds % 3_600 === 0) return unit(seconds / 3_600, 'hour');
  return unit(Math.round(seconds / 60), 'minute');
}

/** `16 Oct 2026, 14:58` in the employee's time zone. */
export const formatDateTime = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Where relative definition links (`docs/features/…#key`) of the API point: the project's documentation in its repository. */
export const DOCS_BASE_URL = 'https://github.com/FrostCoUA/braze-brother/blob/main/';
/** A metric definition link as a URL: absolute ones as they are, repository paths under the documentation base. */
export const definitionHref = (url: string) => (/^https?:\/\//.test(url) ? url : `${DOCS_BASE_URL}${url.replace(/^\/+/, '')}`);
